import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GlassPanel from "@/components/GlassPanel";
import PasswordInput from "@/components/PasswordInput";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { SPORTS as SPORTS_FALLBACK, ATHLETE_CLASSES } from "@/lib/sports";

// Yesterday in YYYY-MM-DD — the max valid DOB (nobody can be born tomorrow).
const MAX_DOB = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", phone: "", password: "", name: "", gender: "M",
    country: "", city: "", dob: "", gym: "",
    athlete_class: "regular", primary_sport: "",
  });
  const [gyms, setGyms] = useState([]);
  const [sports, setSports] = useState(SPORTS_FALLBACK);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/gyms").then((r) => setGyms(r.data.gyms || [])).catch(() => {});
    api.get("/sports").then((r) => setSports(r.data.sports || SPORTS_FALLBACK)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.dob) {
      toast.error("Date of birth is required");
      return;
    }
    setBusy(true);
    try {
      await register({
        ...form,
        gym: form.gym || null,
        primary_sport: form.primary_sport || null,
      });
      toast.success("ATHLETE PROFILE INITIALIZED");
      navigate("/my-gps");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-6 py-16" data-testid="register-page">
      <GlassPanel className="p-10 w-full max-w-2xl" scanline>
        <img
          src="https://customer-assets.emergentagent.com/job_gps-arena-1/artifacts/ys68irlq_GPS%20Logo%20AI-09.png"
          alt="GPS"
          className="h-12 w-12 logo-glow mb-4"
        />
        <div className="gps-tag mb-3">NEW ATHLETE</div>
        <h1 className="gps-heading text-3xl">CALCULATE PROFILE</h1>
        <p className="text-mute text-sm mt-2">
          Initialize your physical signature. Institution can be updated later from your profile.
        </p>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="gps-label">NAME / CALLSIGN</label>
            <input className="gps-input" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
          </div>
          <div>
            <label className="gps-label">EMAIL</label>
            <input type="email" className="gps-input" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email" />
          </div>
          <div>
            <label className="gps-label">PHONE</label>
            <input
              type="tel"
              className="gps-input"
              required
              placeholder="+15551234567"
              pattern="[+0-9\s\(\)\-]{6,20}"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              data-testid="register-phone"
            />
          </div>
          <div>
            <label className="gps-label">PASSWORD</label>
            <PasswordInput
              required
              minLength={6}
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              data-testid="register-password"
              testIdToggle="register-password-toggle"
            />
          </div>
          <div>
            <label className="gps-label">GENDER</label>
            <select className="gps-input" value={form.gender} onChange={(e) => set("gender", e.target.value)} data-testid="register-gender">
              <option value="M">M</option>
              <option value="F">F</option>
            </select>
          </div>
          <div>
            <label className="gps-label">DATE OF BIRTH</label>
            <input
              type="date"
              className="gps-input"
              max={MAX_DOB}
              value={form.dob}
              onChange={(e) => set("dob", e.target.value)}
              data-testid="register-dob"
              aria-required="true"
            />
          </div>
          <div>
            <label className="gps-label">COUNTRY</label>
            <input className="gps-input" value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="register-country" />
          </div>
          <div>
            <label className="gps-label">CITY</label>
            <input className="gps-input" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="register-city" />
          </div>
          <div>
            <label className="gps-label">ATHLETE CLASS</label>
            <select
              className="gps-input"
              value={form.athlete_class}
              onChange={(e) => set("athlete_class", e.target.value)}
              data-testid="register-athlete-class"
            >
              {ATHLETE_CLASSES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="gps-label">PRIMARY SPORT</label>
            <select
              className="gps-input"
              value={form.primary_sport}
              onChange={(e) => set("primary_sport", e.target.value)}
              data-testid="register-primary-sport"
            >
              <option value="">SELECT SPORT</option>
              {sports.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="gps-label">INSTITUTION</label>
            <input
              className="gps-input"
              list="register-gym-options"
              placeholder="Pick or type your institution"
              value={form.gym}
              onChange={(e) => set("gym", e.target.value)}
              data-testid="register-gym"
            />
            <datalist id="register-gym-options">
              {gyms.map((g) => <option key={g} value={g} />)}
            </datalist>
            <div className="text-[10px] gps-mono text-mute mt-1 tracking-[0.15em]">
              CAN&apos;T FIND YOURS? TYPE IT IN — IT&apos;LL BE ADDED AS PENDING APPROVAL · LEAVE EMPTY TO STAY UNAFFILIATED
            </div>
          </div>
          <button type="submit" disabled={busy} className="gps-btn md:col-span-2 mt-2" data-testid="register-submit">
            {busy ? "INITIALIZING..." : "INITIALIZE PROFILE"}
          </button>
        </form>

        <div className="hud-divider my-6" />
        <div className="text-mute text-xs gps-mono text-center">
          ALREADY CALCULATED? <Link to="/login" className="text-white" data-testid="register-to-login">LOGIN</Link>
        </div>
      </GlassPanel>
    </div>
  );
}
